import React from "react";
import { CONCEPT_STICKERS } from "./heroStickers";
import { StickerFamily } from "./stickerClassifier";

// Brand palette pulled from tailwind.config.js / variables.css.
const ELECTRIC_BLUE = "#0affff";
const NEON_PINK = "#ff6ad5";
const HIGHLIGHT = "#5861AC";
const HIGHLIGHT_GREEN = "#8FBC8F";
const HIGHLIGHT_DANGER = "#CD5C5C";
const BLACK = "#000000";
const WHITE = "#FFFFFF";

// Picker tiles are square; the actual sticker is centered inside.
// Final exported PNG uses STICKER_PNG_SIZE so it looks crisp on a 1080-wide IG Story.
export const STICKER_PNG_SIZE = 1080;
export const STICKER_TILE_VIEWBOX = 512;
const VB = STICKER_TILE_VIEWBOX;

type StickerVariantId =
    | "neon-pill"
    | "burst-badge"
    | "graffiti-spray"
    | "ribbon"
    | "comic-pop"
    | "viewfinder";

export interface StickerDef {
    id: string;                  // unique key, e.g. "Degree:1080:burst-badge"
    label?: string;              // human tag text, used for the picker search box
    artworkUrl?: string;         // catalog stickers: remote artwork to embed on place
    family: StickerFamily | "Viewfinder";
    variant: StickerVariantId;
    // Renders the sticker's SVG <g> contents at viewBox 0 0 VB VB.
    // Background is intentionally transparent.
    render: () => React.ReactNode;
}

// ─── Shared SVG primitives ────────────────────────────────────────────────

const DISPLAY_FONT = "Anton, 'Mukta Mahee', sans-serif";

// Wrap `words` into exactly `lines` balanced lines (greedy by per-line char budget).
function wrapInto(words: string[], lines: number): string[] {
    const total = words.join(" ").length;
    const budget = Math.ceil(total / lines) + 2;
    const out: string[] = [];
    let cur = "";
    for (const w of words) {
        if (!cur) cur = w;
        else if ((cur + " " + w).length <= budget) cur += " " + w;
        else { out.push(cur); cur = w; }
    }
    if (cur) out.push(cur);
    return out;
}

const ADV = 0.56; // Anton glyph advance as a fraction of font size

// Pick the line count (1..maxLines) that renders the label LARGEST inside a
// boxW × boxH area, biased toward FEWER lines — so "CORK" and "SWITCH CORK" stay
// one big line on a wide card, while "Goblin Mode" fills a square tile with two
// and "Zoe Send-It-To-The-House Synnott" wraps to three legible ones. Returns the
// chosen lines + fitted font size.
export function fitLines(
    label: string, maxLines: number, boxW: number, boxH: number, capSize = 200,
): { lines: string[]; size: number } {
    const words = label.trim().split(/\s+/).filter(Boolean);
    const single = words.length <= 1;
    const cands: { lines: string[]; size: number }[] = [];
    const maxL = single ? 1 : Math.min(maxLines, words.length);
    for (let L = 1; L <= maxL; L++) {
        const lines = single ? [words[0] || label.trim()] : wrapInto(words, L);
        if (lines.length !== L) continue;
        const longest = lines.reduce((a, b) => (b.length > a.length ? b : a), "");
        const widthFit = boxW / (ADV * Math.max(longest.length, 1));
        const heightFit = boxH / (lines.length * 0.96);
        cands.push({ lines, size: Math.max(16, Math.min(capSize, widthFit, heightFit)) });
    }
    if (!cands.length) return { lines: [label.trim()], size: Math.min(capSize, boxH) };
    const best = Math.max(...cands.map((c) => c.size));
    // Fewest lines whose size is within 82% of the best — only add a line when it
    // buys real legibility.
    return cands.find((c) => c.size >= best * 0.82) ?? cands[0];
}

// Render pre-fitted neon lines centred at (cx, cy) — the shared neon "voice"
// (bold fill + keyline + glow) tying every variant together.
function NeonLines({
    lines, size, cx, cy, fill, stroke = WHITE, strokeWidth = 9, glow, letterSpacing = 1,
}: {
    lines: string[]; size: number; cx: number; cy: number; fill: string;
    stroke?: string; strokeWidth?: number; glow: string; letterSpacing?: number;
}) {
    const lineH = size * 0.96;
    const startY = cy - (lineH * (lines.length - 1)) / 2 + size * 0.34;
    return (
        <g style={{ filter: `drop-shadow(0 0 16px ${glow})` }}>
            {lines.map((ln, i) => (
                <text key={i} x={cx} y={startY + i * lineH}
                    fontFamily={DISPLAY_FONT} fontWeight={700} fontSize={size}
                    textAnchor="middle" letterSpacing={letterSpacing}
                    stroke={strokeWidth > 0 ? stroke : undefined}
                    strokeWidth={strokeWidth > 0 ? strokeWidth : undefined}
                    strokeLinejoin="round" paintOrder="stroke fill" fill={fill}>
                    {ln}
                </text>
            ))}
        </g>
    );
}

// Soft drop shadow + outline used by most pill / badge backings.
function StickerShadow({ children }: { children: React.ReactNode }) {
    return <g style={{ filter: "drop-shadow(0 6px 0 rgba(0,0,0,0.45))" }}>{children}</g>;
}

// ─── Variant renderers ────────────────────────────────────────────────────

// "Neon Plate" — the legible workhorse. Dark plate, glowing neon keyline + inner
// hairline, neon label. Grows taller for multi-line tags.
function NeonPill(label: string, fill: string, glow: string) {
    const cx = VB / 2;
    const cy = VB / 2;
    const { lines, size } = fitLines(label.toUpperCase(), 3, VB * 0.78, VB * 0.5, 150);
    const h = Math.min(VB * 0.72, Math.max(VB * 0.3, lines.length * size * 0.96 + 96));
    const w = VB * 0.92;
    const r = Math.min(h / 2, 56);
    return (
        <>
            <StickerShadow>
                <rect x={cx - w / 2} y={cy - h / 2} rx={r} ry={r} width={w} height={h}
                    fill="#070708" stroke={fill} strokeWidth={13}
                    style={{ filter: `drop-shadow(0 0 22px ${glow})` }} />
                <rect x={cx - w / 2 + 17} y={cy - h / 2 + 17} rx={Math.max(r - 17, 8)} ry={Math.max(r - 17, 8)}
                    width={w - 34} height={h - 34} fill="none" stroke={fill} strokeWidth={2.5} opacity={0.45} />
            </StickerShadow>
            <NeonLines lines={lines} size={size} cx={cx} cy={cy}
                fill={fill} glow={glow} stroke={BLACK} strokeWidth={3} letterSpacing={2} />
        </>
    );
}

// "Starburst Stamp" — spiky badge, black disc, neon ring, label inside.
function BurstBadge(label: string, fill: string, accent: string) {
    const cx = VB / 2;
    const cy = VB / 2;
    const outerR = VB * 0.47;
    const innerR = VB * 0.365;
    const points = 16;
    const pts: string[] = [];
    for (let i = 0; i < points * 2; i++) {
        const r = i % 2 === 0 ? outerR : innerR;
        const angle = (i * Math.PI) / points - Math.PI / 2;
        pts.push(`${cx + r * Math.cos(angle)},${cy + r * Math.sin(angle)}`);
    }
    const { lines, size } = fitLines(label.toUpperCase(), 3, VB * 0.5, VB * 0.44, 120);
    return (
        <>
            <StickerShadow>
                <polygon points={pts.join(" ")} fill={accent} stroke={WHITE} strokeWidth={9}
                    style={{ filter: `drop-shadow(0 0 18px ${accent})` }} />
                <circle cx={cx} cy={cy} r={VB * 0.315} fill="#070708" stroke={WHITE} strokeWidth={7} />
                <circle cx={cx} cy={cy} r={VB * 0.275} fill="none" stroke={accent} strokeWidth={2.5} opacity={0.6} />
            </StickerShadow>
            <NeonLines lines={lines} size={size} cx={cx} cy={cy}
                fill={WHITE} glow={accent} stroke={BLACK} strokeWidth={3} />
        </>
    );
}

// "Spray Tag" — boxless sprayed stencil: heavy outlined glowing type over a
// sprayed slash with drips. The type IS the sticker.
function GraffitiSpray(label: string, fill: string) {
    const cx = VB / 2;
    const cy = VB / 2;
    const { lines, size } = fitLines(label.toUpperCase(), 2, VB * 0.9, VB * 0.46, 180);
    return (
        <>
            <g style={{ filter: `drop-shadow(0 0 18px ${fill})` }} opacity={0.92}>
                <path
                    d={`M ${VB * 0.1} ${cy + VB * 0.21} Q ${cx} ${cy + VB * 0.31} ${VB * 0.9} ${cy + VB * 0.17}`}
                    stroke={fill} strokeWidth={22} fill="none" strokeLinecap="round"
                />
                <circle cx={VB * 0.26} cy={cy + VB * 0.28} r={5} fill={fill} />
                <circle cx={VB * 0.5} cy={cy + VB * 0.325} r={6.5} fill={fill} />
                <circle cx={VB * 0.73} cy={cy + VB * 0.27} r={4} fill={fill} />
            </g>
            <NeonLines lines={lines} size={size} cx={cx} cy={cy - VB * 0.04}
                fill={fill} glow={fill} stroke={BLACK} strokeWidth={10} letterSpacing={3} />
        </>
    );
}

// "Banner" — varsity ribbon with end-folds for depth; bold black label on the
// bright family color (palette variety against the dark plate/badge tiles).
function Ribbon(label: string, fill: string, accent: string) {
    const cx = VB / 2;
    const cy = VB / 2;
    const w = VB * 0.96;
    const notch = 34;
    const { lines, size } = fitLines(label.toUpperCase(), 2, w - notch * 2 - 28, VB * 0.34, 118);
    const h = Math.min(VB * 0.56, Math.max(VB * 0.26, lines.length * size * 0.96 + 56));
    const d = `M ${cx - w / 2} ${cy - h / 2} L ${cx + w / 2} ${cy - h / 2} L ${cx + w / 2 - notch} ${cy} L ${cx + w / 2} ${cy + h / 2} L ${cx - w / 2} ${cy + h / 2} L ${cx - w / 2 + notch} ${cy} Z`;
    const foldL = `M ${cx - w / 2} ${cy - h / 2} L ${cx - w / 2 - 28} ${cy - h / 2 + 20} L ${cx - w / 2} ${cy} Z`;
    const foldR = `M ${cx + w / 2} ${cy - h / 2} L ${cx + w / 2 + 28} ${cy - h / 2 + 20} L ${cx + w / 2} ${cy} Z`;
    return (
        <>
            <StickerShadow>
                <path d={foldL} fill={accent} opacity={0.5} />
                <path d={foldR} fill={accent} opacity={0.5} />
                <path d={d} fill={fill} stroke={BLACK} strokeWidth={9}
                    style={{ filter: `drop-shadow(0 0 16px ${accent})` }} />
                <path d={d} fill="none" stroke={WHITE} strokeWidth={2.5} opacity={0.5} transform="translate(0,5)" />
            </StickerShadow>
            <NeonLines lines={lines} size={size} cx={cx} cy={cy}
                fill={BLACK} glow="transparent" stroke={WHITE} strokeWidth={4} />
        </>
    );
}

// "Pop Block" — pop-art: concentric burst rings + skewed duotone offset shadow.
function ComicPop(label: string, fill: string, accent: string) {
    const cx = VB / 2;
    const cy = VB / 2;
    const { lines, size } = fitLines(label.toUpperCase(), 2, VB * 0.84, VB * 0.48, 160);
    return (
        <>
            <g opacity={0.16}>
                {Array.from({ length: 5 }).map((_, i) => (
                    <circle key={i} cx={cx} cy={cy} r={VB * 0.16 + i * VB * 0.07} fill="none" stroke={accent} strokeWidth={2} />
                ))}
            </g>
            <g transform="translate(15,18) skewX(-9)">
                <NeonLines lines={lines} size={size} cx={cx} cy={cy}
                    fill={BLACK} glow={accent} stroke={accent} strokeWidth={11} letterSpacing={2} />
            </g>
            <g transform="skewX(-9)">
                <NeonLines lines={lines} size={size} cx={cx} cy={cy}
                    fill={fill} glow={accent} stroke={WHITE} strokeWidth={9} letterSpacing={2} />
            </g>
        </>
    );
}

// ─── Family → palette mapping ─────────────────────────────────────────────

const familyPalette: Record<Exclude<StickerFamily, "Generic">, { fill: string; accent: string }> = {
    Direction: { fill: ELECTRIC_BLUE, accent: ELECTRIC_BLUE },
    Sauce:     { fill: NEON_PINK,     accent: NEON_PINK },
    Axis:      { fill: HIGHLIGHT_GREEN, accent: HIGHLIGHT_GREEN },
    Degree:    { fill: ELECTRIC_BLUE, accent: NEON_PINK },
    Grab:      { fill: WHITE,         accent: ELECTRIC_BLUE },
    Style:     { fill: HIGHLIGHT,     accent: ELECTRIC_BLUE },
    Feature:   { fill: HIGHLIGHT_DANGER, accent: NEON_PINK },
    Activity:  { fill: HIGHLIGHT_GREEN,  accent: ELECTRIC_BLUE },
};

function paletteFor(family: StickerFamily) {
    if (family === "Generic") return { fill: WHITE, accent: ELECTRIC_BLUE };
    return familyPalette[family];
}

// ─── Public API: build sticker variants for a given tag ───────────────────

export function buildTagStickers(tag: string, family: StickerFamily): StickerDef[] {
    // Hand-tuned hero stickers win over generic variants. We surface the hero
    // FIRST so it shows up at the front of the tray, and follow it with the
    // 5 generic variants so users still have stylistic options.
    const heroOverrides = CONCEPT_STICKERS[tag.trim().toLowerCase()] ?? [];

    const { fill, accent } = paletteFor(family);
    const baseId = `${family}:${tag}`;
    const defaults: StickerDef[] = [
        {
            id: `${baseId}:neon-pill`,
            label: tag,
            family,
            variant: "neon-pill",
            render: () => NeonPill(tag, fill, accent),
        },
        {
            id: `${baseId}:burst-badge`,
            label: tag,
            family,
            variant: "burst-badge",
            render: () => BurstBadge(tag, WHITE, accent),
        },
        {
            id: `${baseId}:graffiti-spray`,
            label: tag,
            family,
            variant: "graffiti-spray",
            render: () => GraffitiSpray(tag, fill),
        },
        {
            id: `${baseId}:ribbon`,
            label: tag,
            family,
            variant: "ribbon",
            render: () => Ribbon(tag, fill, accent),
        },
        {
            id: `${baseId}:comic-pop`,
            label: tag,
            family,
            variant: "comic-pop",
            render: () => ComicPop(tag, fill, accent),
        },
    ];

    return [...heroOverrides, ...defaults];
}

// ─── Viewfinder ("camera recording border") sticker ───────────────────────
// Designed to overlay the whole IG story (9:16) — drawn here at square VB,
// but the actual export uses 9:16 aspect (1080×1920).

const VIEWFINDER_W = 1080;
const VIEWFINDER_H = 1920;

export interface ViewfinderProps {
    username: string;
}

// Viewfinder text runs vertically up the side edges, magazine-spine style.
// IG's UI chrome covers the top and bottom of the canvas but NEVER the side
// edges, so vertical text along the sides is always visible — and it frees
// up the entire central vertical area for the clip + user-placed stickers.

// Anchor positions for the vertical text — placed at the BOTTOM of each edge,
// then rotated -90° so the text reads bottom-up along the side.
const LEFT_SPINE_X = 92;
const RIGHT_SPINE_X = 988;
const SPINE_BOTTOM_Y = 1640;   // baseline anchor, above IG's bottom chrome
const SPINE_TOP_Y = 980;       // top anchor for the @handle, which reads DOWN

export function ViewfinderSticker({ username }: ViewfinderProps) {
    const cornerLen = 140;
    const stroke = 14;
    const inset = 60;
    const handle = username.startsWith("@") ? username : `@${username}`;

    return (
        <svg
            xmlns="http://www.w3.org/2000/svg"
            viewBox={`0 0 ${VIEWFINDER_W} ${VIEWFINDER_H}`}
            width="100%"
            height="100%"
        >
            {/* corner brackets — purely decorative; the brackets at the canvas
                edges sit under IG's chrome but they're symmetrical so the eye
                reads "viewfinder framing" rather than missing content */}
            {[
                `M ${inset} ${inset + cornerLen} V ${inset} H ${inset + cornerLen}`,
                `M ${VIEWFINDER_W - inset - cornerLen} ${inset} H ${VIEWFINDER_W - inset} V ${inset + cornerLen}`,
                `M ${VIEWFINDER_W - inset} ${VIEWFINDER_H - inset - cornerLen} V ${VIEWFINDER_H - inset} H ${VIEWFINDER_W - inset - cornerLen}`,
                `M ${inset + cornerLen} ${VIEWFINDER_H - inset} H ${inset} V ${VIEWFINDER_H - inset - cornerLen}`,
            ].map((d, i) => (
                <path key={i} d={d} stroke={WHITE} strokeWidth={stroke} fill="none" strokeLinecap="square"
                    style={{ filter: `drop-shadow(0 0 12px ${ELECTRIC_BLUE})` }} />
            ))}

            {/* LEFT SPINE — REC dot + ECLIPTIC, running bottom-up.
                Translate to the anchor, then rotate -90° so the text climbs the edge. */}
            <g transform={`translate(${LEFT_SPINE_X}, ${SPINE_BOTTOM_Y}) rotate(-90)`}>
                <circle cx={-12} cy={-22} r={20} fill={HIGHLIGHT_DANGER}
                    style={{ filter: `drop-shadow(0 0 14px ${HIGHLIGHT_DANGER})` }} />
                <text x={26} y={0} fill={WHITE}
                    fontFamily="'Mukta Mahee', system-ui, sans-serif"
                    fontWeight={700} fontSize={72} letterSpacing={8}
                    stroke={BLACK} strokeWidth={5} paintOrder="stroke fill"
                    style={{ filter: `drop-shadow(0 0 12px ${ELECTRIC_BLUE})` }}>
                    ECLIPTIC
                </text>
            </g>

            {/* RIGHT SPINE — @username, running top-DOWN the right edge (mirrors
                ECLIPTIC, which climbs the left edge). rotate(+90) from a top
                anchor makes the first glyph sit at SPINE_TOP_Y and the rest
                advance downward. */}
            <g transform={`translate(${RIGHT_SPINE_X}, ${SPINE_TOP_Y}) rotate(90)`}>
                <text x={0} y={0} fill={ELECTRIC_BLUE}
                    fontFamily="'Mukta Mahee', system-ui, sans-serif"
                    fontWeight={700} fontSize={64} letterSpacing={6}
                    stroke={BLACK} strokeWidth={5} paintOrder="stroke fill"
                    style={{ filter: `drop-shadow(0 0 12px ${ELECTRIC_BLUE})` }}>
                    {handle}
                </text>
            </g>
        </svg>
    );
}

// Helper: render a tile-sized sticker SVG (square) used in the picker grid
// and as the source of the PNG export.
export function StickerSvg({
    sticker,
    size = STICKER_TILE_VIEWBOX,
}: {
    sticker: StickerDef;
    size?: number;
}) {
    return (
        <svg
            xmlns="http://www.w3.org/2000/svg"
            viewBox={`0 0 ${VB} ${VB}`}
            width={size}
            height={size}
        >
            {sticker.render()}
        </svg>
    );
}

// ─── Catalog stickers (the illustrated "General" pack from the backend) ────
// A catalog sticker is remote artwork (SVG/PNG hosted in blob). The picker tile
// renders it directly via <image href={remoteUrl}>; on PLACE the editor re-embeds
// the artwork as a data: URL (embedImage) so the share composite stays taint-free
// — exactly like Pins. `artworkUrl` carries the source so the editor can embed.
export function catalogStickerDef(id: string, label: string, href: string): StickerDef {
    return {
        id,
        label,
        artworkUrl: href,
        family: "Generic",
        variant: "catalog" as never,
        render: () => (
            <image href={href} xlinkHref={href} x={0} y={0} width={VB} height={VB}
                preserveAspectRatio="xMidYMid meet" />
        ),
    };
}

export function buildCatalogStickers(
    designs: { id: string; label: string; artwork_url: string }[],
): StickerDef[] {
    return designs.map((d) => catalogStickerDef(`design:${d.id}`, d.label, d.artwork_url));
}

export const VIEWFINDER_DIMENSIONS = { width: VIEWFINDER_W, height: VIEWFINDER_H };
export const STICKER_DIMENSIONS = { width: VB, height: VB };
